import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { t } from "i18next";

import { Box, CircularProgress, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import {
  containerSx,
  emptyStateSx,
  loadingOverlaySx,
  scrollContainerSx,
  statusBarSx,
  tableSx
} from "@/src/components/organisms/AdminDataTable/adminDataTable.styles";
import {
  AdminDataTableProps,
  DENSITY_ROW_HEIGHT,
  DensityMode
} from "@/src/components/organisms/AdminDataTable/adminDataTable.types";
import AdminDataTableBody from "@/src/components/organisms/AdminDataTable/AdminDataTableBody";
import AdminDataTableHeader from "@/src/components/organisms/AdminDataTable/AdminDataTableHeader";
import AdminDataTableToolbar from "@/src/components/organisms/AdminDataTable/AdminDataTableToolbar";
import { ColumnFilterMeta } from "@/src/components/organisms/AdminDataTable/ColumnFilterInput";
import {
  ColumnFiltersState,
  OnChangeFn,
  VisibilityState,
  filterFn_includesString,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@/src/components/organisms/AdminDataTable/reactTable";

const readStoredDensity = (
  storageKey: string | undefined
): DensityMode | null => {
  if (!storageKey) return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw === "compact" || raw === "standard" || raw === "comfortable") {
      return raw;
    }
  } catch {
    return null;
  }
  return null;
};

const AdminDataTable = <TData extends object>(
  props: AdminDataTableProps<TData>
) => {
  const {
    data,
    columns,
    getRowId,
    initialSorting,
    initialColumnPinning,
    initialColumnVisibility,
    height,
    maxWidth,
    densityStorageKey,
    emptyMessage,
    loadingMessage,
    isLoading = false,
    isLoadingMore = false,
    loadMoreSlot,
    exportFileName = "export",
    enableCsvExport = true,
    toolbarTitle,
    toolbarSearchExtras,
    toolbarExtras,
    scrollToRowId,
    onScrolledToRow
  } = props;

  const [density, setDensity] = useState<DensityMode>(
    () => readStoredDensity(densityStorageKey) ?? "compact"
  );

  useEffect(() => {
    if (!densityStorageKey) return;
    try {
      sessionStorage.setItem(densityStorageKey, density);
    } catch {
      // ignore storage errors
    }
  }, [density, densityStorageKey]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [filterResetVersion, setFilterResetVersion] = useState(0);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => initialColumnVisibility ?? {}
  );

  const [scrollAtEnd, setScrollAtEnd] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setScrollAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 20);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  const handleScrollToggle = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      left: scrollAtEnd ? 0 : el.scrollWidth,
      behavior: "smooth"
    });
  }, [scrollAtEnd]);

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (
    updater
  ) => {
    setColumnVisibility((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );
  };

  const handleResetFilters = () => {
    setColumnFilters([]);
    setGlobalFilter("");
    setFilterResetVersion((version) => version + 1);
  };

  useEffect(() => {
    if (scrollToRowId) handleResetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToRowId]);

  const table = useReactTable<TData>({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      columnFilters,
      globalFilter,
      columnVisibility
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    initialState: {
      sorting: initialSorting ?? [],
      columnPinning: initialColumnPinning ?? { start: [], end: [] }
    },
    globalFilterFn: filterFn_includesString
  });

  const hasAnyColumnFilter = useMemo(
    () =>
      columns.some((col) => {
        const meta = col.meta as ColumnFilterMeta | undefined;
        return Boolean(meta?.filterVariant);
      }),
    [columns]
  );

  const rowHeight = DENSITY_ROW_HEIGHT[density];
  const columnCount = table.getVisibleLeafColumns().length;
  const rowCount = table.getRowModel().rows.length;
  const totalRowCount = table.getPreFilteredRowModel().rows.length;

  const hasActiveFilters = globalFilter.length > 0 || columnFilters.length > 0;

  const resultsLabel = hasActiveFilters
    ? t("admin.filteredResultsCount", {
        count: rowCount,
        total: totalRowCount
      })
    : t("admin.resultsCount", { count: totalRowCount });

  return (
    <Box sx={[containerSx, { height, maxWidth: maxWidth ?? "100%" }]}>
      <AdminDataTableToolbar
        table={table}
        density={density}
        onDensityChange={setDensity}
        exportFileName={exportFileName}
        title={toolbarTitle}
        globalFilter={globalFilter}
        filterResetVersion={filterResetVersion}
        hasActiveFilters={hasActiveFilters}
        onResetFilters={handleResetFilters}
        enableCsvExport={enableCsvExport}
        isLoading={isLoading}
        searchExtras={toolbarSearchExtras}
        extras={toolbarExtras}
        scrollAtEnd={scrollAtEnd}
        onScrollToggle={handleScrollToggle}
      />
      <Box ref={scrollRef} sx={scrollContainerSx}>
        <Box
          component="table"
          role="table"
          sx={tableSx}
          style={{ width: table.getTotalSize() }}
        >
          <AdminDataTableHeader
            table={table}
            hasAnyColumnFilter={hasAnyColumnFilter}
            filterResetVersion={filterResetVersion}
          />
          <AdminDataTableBody
            table={table}
            rowHeight={rowHeight}
            scrollRef={scrollRef}
            columnCount={columnCount}
            scrollToRowId={scrollToRowId}
            onScrolledToRow={onScrolledToRow}
          />
        </Box>
        {rowCount === 0 && !isLoading && (
          <Box sx={emptyStateSx}>
            <Typography variant="body2">
              {emptyMessage ?? t("admin.dataTable.emptyMessage")}
            </Typography>
          </Box>
        )}
        {isLoading && (
          <Box
            sx={loadingOverlaySx}
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <CircularProgress size={36} />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary
              }}
            >
              {loadingMessage ?? t("admin.dataTable.loadingMessage")}
            </Typography>
          </Box>
        )}
      </Box>
      {loadMoreSlot && (
        <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
          {loadMoreSlot}
        </Box>
      )}
      <Box sx={statusBarSx} role="status" aria-live="polite">
        <Typography variant="caption" color="inherit">
          {resultsLabel}
        </Typography>
        {isLoadingMore && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CircularProgress size={12} />
            <Typography variant="caption" color="inherit">
              {t("admin.dataTable.loadingMoreStatus")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminDataTable;
